import { GUID } from "@faro-lotv/foundation";
import { create } from "zustand";

type CaptureTreeSelectionStore = {
  /** The list of project items selected by the user */
  selectedIds: Set<GUID>;
  /** A callback to select multiple project items at the same time */
  multiSelectIds(selectedIds: GUID[]): void;
  /** A callback to toggle the selection state of a project item */
  toggleSelectedId(selectedId: GUID): void;
  /** A callback to clear the list of selected elements */
  clearSelection(): void;
  /** IDs of the hovered project items, if any */
  hoveredIds: GUID[];
  /** Sets the only hovered project item ID */
  setHoveredId(hoveredId: GUID | null): void;
  /** Sets the hovered project item IDs */
  setHoveredIds(hoveredIds: GUID[]): void;
  /** Update the selection using a custom update callback */
  updateSelection(update: (selectedIds: Set<GUID>) => Set<GUID>): void;
};

/**
 * A store object suitable to manage temporary hovering/selection state of the capture tree elements
 */
export const useCaptureTreeSelectionStore = create<CaptureTreeSelectionStore>()(
  (set, getState) => ({
    selectedIds: new Set<GUID>(),
    hoveredIds: [],
    multiSelectIds: (selectedIds: GUID[]) =>
      set({ selectedIds: new Set<GUID>(selectedIds) }),
    toggleSelectedId: (selectedId: GUID) => {
      const state = getState();

      const selectedIds = new Set<GUID>(state.selectedIds);
      if (selectedIds.has(selectedId)) {
        selectedIds.delete(selectedId);
      } else {
        selectedIds.add(selectedId);
      }
      set({ selectedIds });
    },
    clearSelection: () => set({ selectedIds: new Set<GUID>() }),
    setHoveredId: (hoveredId: GUID | null) => {
      set({ hoveredIds: hoveredId ? [hoveredId] : [] });
    },
    setHoveredIds: (hoveredIds: GUID[]) => set({ hoveredIds }),
    updateSelection: (update: (selectedIds: Set<GUID>) => Set<GUID>) => {
      set({ selectedIds: update(getState().selectedIds) });
    },
  }),
);
